/**
 * Be You Esthetic Suites — bespoke Category + Service views.
 *
 * Direct port of the design handoff (booking-software-ui project):
 *   - chats/chat1.md (intent)
 *   - beyou-views.jsx (BeYouCategoryStep)
 *   - beyou-service-v2.jsx (BeYouServiceRow / BeYouServiceStep — expandable
 *     card with variant pickers)
 *   - beyou-data.jsx (BEYOU_CATEGORIES + BEYOU_SERVICES)
 *
 * Only rendered when bootstrap.branding.customViewBundle === 'be-you'.
 * Every other tenant goes through the shared `ServiceStep` component — H22.
 *
 * Data is hardcoded here (mirroring beyou-data.jsx) because:
 *   - GHL's /calendars/ endpoint doesn't surface category descriptions,
 *     "from" prices, popular-service callouts, or variant pricing tiers
 *     (sessions / area / area_then_sessions / subscription / packages).
 *   - These are CRO copy + commercial structure decided by the med-spa,
 *     not auto-derivable from booking-provider metadata.
 *   - The booking flow still uses real GHL services for slot search +
 *     checkout — only the displayed catalog is hardcoded.
 */

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import type { ShopProduct } from '../types';
import {
  buildCatalog,
  type BeYouCategory,
  type BeYouService,
  type BeYouTier,
  type Catalog,
} from '../lib/beyou-catalog';

// ── Search icon (mirrors the design's Icon.Search) ──
function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  );
}

// ── Star icon (badges) ──
function StarIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77 5.82 21l1.18-6.88-5-4.87 6.91-1.01L12 2z" />
    </svg>
  );
}

// ── Be You category catalog (mirrors beyou-data.jsx BEYOU_CATEGORIES) ──

// Sourced from /Users/matt/Ai Coding/Be You Esthetic/beyouesthetic-site-report.md
// Section 2 (Services, Descriptions & Prices) — categories A through G,
// skipping F (Memberships) since the live page is broken on the site.
// `count` reflects the number of services we render inside each category
// (matches the BEYOU_SERVICES arrays below). `from` is the minimum
// single-session price in that category.
const BEYOU_CATEGORIES: BeYouCategory[] = [
  // Visible lead-capture category — a single free-consultation card that
  // books a $0 appointment against a dedicated GHL calendar. Kept first so
  // it reads as "Not sure where to start?" before the priced menu. The $0
  // booking only goes through if a WidgetServiceOverride row exists for the
  // consult calendar (priceCentsOverride=0 + depositType=NONE) — seeded per
  // tenant by scripts/seed-be-you-consultation.ts.
  {
    id: 'consults',
    name: 'Consultations',
    sub: 'Not sure where to start? Book a free phone consultation with our team and we’ll map out the right treatment plan for your goals.',
    count: 2,
    from: 0,
    blurb: 'Free Consultation',
  },
  {
    id: 'face',
    name: 'Face & Skin Treatments',
    sub: 'Microdermabrasion, peels, acne facials, lymphatic face massage, laser hair removal, Botox, microneedling, Kybella, fillers, waxing (à la carte + packages)',
    count: 11,
    from: 25,
    blurb: 'Face & Skin Treatments',
  },
  {
    id: 'body',
    name: 'Body Contouring & Sculpting',
    sub: 'Red light therapy, ultrasonic cavitation, vacuum butt lift, wood therapy, manual sculpting, lymphatic drainage (body + face)',
    count: 7,
    from: 75,
    blurb: 'Body Contouring & Sculpting',
  },
  {
    id: 'weight',
    name: 'Medical Weight Management',
    sub: 'Medically supervised Semaglutide & Tirzepatide GLP-1 programs — virtual evaluation, home delivery',
    count: 2,
    from: 600,
    perMo: true,
    blurb: 'Medical Weight Management — GLP-1',
  },
  // Hidden — Be You Skin Club member-perk services. Accessible only via
  // `?category=skin-club` deep link from the member portal. Not in the
  // public tab bar, search, or All view.
  {
    id: 'skin-club',
    name: 'Be You Skin Club',
    sub: 'Member-included services — book your monthly perks',
    count: 3,
    from: 0,
    blurb: 'Be You Skin Club · member benefits',
    hidden: true,
  },
  // Hidden — event packages (party / bridal / dual glow-up). Inquiry-
  // only calendars: customer books a quick consult slot, staff follows
  // up off-widget for pricing/details. Reach via `?category=events`
  // or per-service `?service=<id>`.
  {
    id: 'events',
    name: 'Event Packages',
    sub: 'Party, bridal & dual glow-up bookings — request a date and we’ll follow up with details',
    count: 3,
    from: 0,
    blurb: 'Be You Event Packages',
    hidden: true,
  },
];

// ── Be You service catalog (mirrors beyou-data.jsx BEYOU_SERVICES) ──

// Lymphatic Face Massage is listed under BOTH Face & Skin and Body
// Contouring (client request 2026-08-10) — one shared object referenced
// from both category arrays. flattenAllServices dedupes by object
// identity, so the "All" tab and search render it once (tagged with the
// first category that carries it), while each category tab still shows
// it. Desc mirrors the GHL calendar description; price is the client's
// ($125 → standard $50 single-session deposit via the deposit rule).
// GHL has a second identical calendar (DLSOgDxrTsbw4nbPOVSm) — the
// client keeps ImZK1txnLk6ba2iJcKhk; don't wire the duplicate.
const LYMPHATIC_FACE_MASSAGE: BeYouService = {
  id: 'lymph-face',
  name: 'Lymphatic Face Massage',
  desc: 'Massage to promote lymph drainage in the face, reducing puffiness and supporting skin health.',
  duration: 60,
  variant: 'flat',
  price: 125,
  ghlCalendarId: 'ImZK1txnLk6ba2iJcKhk',
};

/**
 * Calendars this curated menu offers that are NOT in the browsable
 * catalogue — GHL `personal` / round-robin types, which the provider
 * excludes from `availableForBookingOnly`.
 *
 * The bootstrap has to be told about them so it can mint an unlisted
 * grant for each; without one they render in the menu and then fail at
 * add-to-cart with "not available for booking". Be You's GLP-1
 * consultation did exactly that from the day it shipped.
 */
export const BE_YOU_UNLISTED_CALENDAR_IDS = ['kGhZWvYDeH4gp1AHaFt9'] as const;

const BEYOU_SERVICES: Record<string, BeYouService[]> = {
  // Free consultation — `consult` variant renders a "Book a consultation →"
  // CTA, passes $0 price / $0 deposit, and books against this GHL calendar.
  // Payment is auto-skipped (getDeposit resolves to $0) and cart.checkout's
  // free-booking branch skips Stripe — provided the per-tenant
  // WidgetServiceOverride (priceCentsOverride=0, depositType=NONE) is seeded.
  consults: [
    {
      id: 'consult-free',
      name: 'Free Consultation',
      desc: 'Not sure where to start? Book a complimentary 15-minute phone consultation with our team to map out the right treatments for your goals. No charge, no commitment.',
      duration: 15,
      variant: 'consult',
      ghlCalendarId: 'bwfeQI0Q7zIZBux4EUQf',
    },
    {
      id: 'consult-glp1',
      // Renamed for clients 2026-08-27 (Matt). The GHL calendar keeps its
      // own name — "FREE GLP-1 Consultation" — so their confirmations and
      // automations are untouched. This curated menu is hand-written, so
      // the DB nameOverride does not reach it; the two are kept in step
      // by hand.
      name: 'GLP-1 Phone Consultation',
      desc: 'Considering medically supervised weight loss? Book a complimentary 30-minute phone consultation to talk through our Semaglutide and Tirzepatide GLP-1 programs and whether they’re right for you. No charge, no commitment.',
      duration: 30,
      variant: 'consult',
      ghlCalendarId: 'kGhZWvYDeH4gp1AHaFt9',
    },
  ],
  face: [
    {
      id: 'micro',
      name: 'Microdermabrasion Facial',
      desc: 'Non-invasive exfoliation that smooths fine lines, sun damage, and acne scars for radiant, even-toned skin. Minimal downtime.',
      duration: 60,
      variant: 'sessions',
      tiers: [
        { id: 1, label: '1 session', price: 180, ghlCalendarId: 'jhfH31JmuaOVBfQaaWoK' },
        { id: 3, label: '3 sessions', price: 480, save: 60, ghlCalendarId: 'OkoFqHKl5A9f4Ldr3yYW' },
        { id: 6, label: '6 sessions', price: 900, save: 180, ghlCalendarId: 'XASywOMHPT0dC8xU3mJ2' },
      ],
      badge: 'Most booked',
    },
    {
      id: 'peel',
      name: 'Chemical Peel',
      desc: 'Professional skin-resurfacing that reduces fine lines, scars, and uneven texture. Customized to your skin type.',
      duration: 45,
      variant: 'area_then_sessions',
      areas: [
        { id: 'sm', label: 'Face or neck', tiers: [
          { id: 1, label: '1 session', price: 199, ghlCalendarId: 'khf5jGVCXV49TSC6Iv4S' },
          { id: 3, label: '3 sessions', price: 549, save: 48, ghlCalendarId: 'BqGL4nckQT9A3UJMDOK2' },
        ] },
        { id: 'lg', label: 'Back / chest / leg / butt', tiers: [
          { id: 1, label: '1 session', price: 450, ghlCalendarId: 'jVTseOkHHBPyPMCZ5XGq' },
          { id: 3, label: '3 sessions', price: 1260, save: 90, ghlCalendarId: 'ddlii4oQaJLVyyWBY9Oe' },
        ] },
      ],
    },
    {
      id: 'acne',
      name: 'Facial Acne Treatment',
      desc: 'Comprehensive acne facial: skin analysis, double cleanse, microdermabrasion, deep-pore mask, and rebalancing moisturizer.',
      duration: 60,
      variant: 'area_then_sessions',
      areas: [
        { id: 'sm', label: 'Face or neck', tiers: [
          { id: 1, label: '1 session', price: 199, ghlCalendarId: 'rr6iR6R2ZBtjYSreJ4OB' },
          { id: 3, label: '3 sessions', price: 549, save: 48, ghlCalendarId: 'y6EEXjySfTaQuQAfJ8is' },
        ] },
        { id: 'lg', label: 'Back / chest / leg / butt', tiers: [
          { id: 1, label: '1 session', price: 450, ghlCalendarId: 'MsBIEexe7lCuqfRlPGig' },
          { id: 3, label: '3 sessions', price: 1260, save: 90, ghlCalendarId: 'SY3lNBH0qO3L8uiI5WN1' },
        ] },
      ],
    },
    LYMPHATIC_FACE_MASSAGE,
    {
      id: 'lhr',
      name: 'Laser Hair Removal',
      desc: 'FDA-approved laser treatment to permanently reduce hair growth. Priced by body-area size.',
      duration: 45,
      variant: 'area_then_sessions',
      areas: [
        { id: 'sm', label: 'Small · lip, chin, sideburns, underarms', tiers: [
          { id: 1, label: '1 session', price: 60, ghlCalendarId: 'HZOxrr5lZlYgpZKoU6oS' },
          { id: 6, label: '6 sessions', price: 350, save: 10, ghlCalendarId: 'baT6gdmlhzzRSxUkG15w' },
        ] },
        { id: 'md', label: 'Medium · Brazilian, bikini, half legs/arms', tiers: [
          { id: 1, label: '1 session', price: 150, ghlCalendarId: 'AH2r1ebJrtxy9mEaXS8D' },
          { id: 6, label: '6 sessions', price: 650, save: 250, ghlCalendarId: 'fn9XceoRrYs8osxTFnT1' },
        ] },
        { id: 'lg', label: 'Large · full legs, arms, back, chest', tiers: [
          { id: 1, label: '1 session', price: 250, ghlCalendarId: 'kn1GeN0RXR3YopTxXpg5' },
          { id: 6, label: '6 sessions', price: 1250, save: 250, ghlCalendarId: '5L8pHwDc0HAiLZHSFciV' },
        ] },
      ],
    },
    {
      id: 'botox',
      name: 'Botox',
      desc: "Injectable wrinkle treatment for crow's feet, forehead lines, and frown lines.",
      duration: 30,
      variant: 'areas_count',
      tiers: [
        { id: 1, label: '1 area · 20 units', price: 250, ghlCalendarId: 'oLuwDF2w0nq1ND8ftGSZ' },
        { id: 2, label: '2 areas · 40 units', price: 500, ghlCalendarId: '1ULE572nDhaCzl5Km0QI' },
        { id: 3, label: '3 areas · 60 units', price: 750, ghlCalendarId: 'KmGcA0PCNss6qeHsjFlw' },
      ],
      addon: { label: 'Additional units', unit: 15 },
    },
    {
      id: 'mn',
      name: 'Microneedling',
      desc: 'Minimally invasive treatment that boosts collagen and elastin for improved tone, texture, and radiance.',
      duration: 60,
      variant: 'sessions',
      tiers: [
        { id: 1, label: '1 session', price: 300, ghlCalendarId: 'ZHP9lByG77HDdqSQLJ5e' },
        { id: 3, label: '3 sessions', price: 925, save: 55, ghlCalendarId: 'Yft9tMV4KywrFTNYBl48' },
        { id: 5, label: '5 sessions', price: 1470, save: 163, ghlCalendarId: '6q21jdX7M5Se2XfXzkkK' },
      ],
    },
    {
      id: 'kybella',
      name: 'Kybella Chin Treatment',
      desc: 'FDA-approved non-surgical injectable that destroys submental ("double chin") fat for a contoured jawline.',
      duration: 30,
      variant: 'flat',
      price: 650,
      unitLabel: 'per vial',
      ghlCalendarId: '2GGhhf59U5erzKjDNsoH',
    },
    {
      id: 'fillers',
      name: 'Body Fillers',
      desc: 'Add volume and contour with Body Fillers. Perfect for enhancing features and creating a more youthful, defined appearance — immediate, natural-looking results with minimal downtime.',
      duration: 60,
      variant: 'packages',
      tiers: [
        { id: 'rha',     label: 'RHA / Restylane / Versa',                    price: 700,  ghlCalendarId: 'xBBOyB8GM3V3JlN4BKZb' },
        { id: 'voluma',  label: 'Voluma / Volbella',                          price: 850,  ghlCalendarId: 'GdPiJmdMoTpGuXLT2KsN' },
        { id: 'hylenex', label: 'Hylenex · filler reversal',                  price: 500,  ghlCalendarId: 'V0MKS7oGd5V6c0rRYrrl' },
        { id: 'bbl',     label: 'Brazilian Butt Lift w/ Fillers (10 vials)',  price: 3800, ghlCalendarId: 'eH6QDsx4xhZJXYQBy6Kx' },
      ],
    },
    {
      id: 'wax-single',
      name: 'Waxing',
      desc: 'Hair removal at the root by independent licensed estheticians. Pick the area you want — or bundle multiple areas into a Waxing Package below for a better rate.',
      duration: 30,
      variant: 'packages',
      tiers: [
        { id: 'brows',     label: 'Brows',         price: 25, ghlCalendarId: 'xPQElfcv3yyQcxZArlC8' },
        { id: 'lipchin',   label: 'Lip / Chin',    price: 25, ghlCalendarId: 'YRG8GvF30AnHZlkyl6At' },
        { id: 'underarms', label: 'Underarms',     price: 35, ghlCalendarId: '7iA3T6OUNs6oNGWKi28B' },
        { id: 'armshalf',  label: 'Arms (Half)',   price: 35, ghlCalendarId: 'PoWi3Low7zdkuiU3xKNS' },
        { id: 'bikini',    label: 'Bikini',        price: 45, ghlCalendarId: '4tzhF9gDgOeUYXtdj0OQ' },
        { id: 'legshalf',  label: 'Legs (Half)',   price: 45, ghlCalendarId: '2yX2yt6M6tL7sTLxplLt' },
        { id: 'armsfull',  label: 'Arms (Full)',   price: 60, ghlCalendarId: '6rx4hV9OYG05BnURTqZD' },
        { id: 'brazilian', label: 'Brazilian',     price: 75, ghlCalendarId: '6XUabHwvho0eX5LBuC2b' },
        { id: 'fullface',  label: 'Full Face',     price: 80, ghlCalendarId: 'UBQ27bX22T9zprTin8qC' },
        { id: 'backchest', label: 'Back / Chest',  price: 90, ghlCalendarId: 'VoaVIg5vUX43nS8XxmF3' },
        { id: 'legsfull',  label: 'Legs (Full)',   price: 90, ghlCalendarId: 'WjGJAD6WPEaANTCsEG6v' },
      ],
    },
    {
      id: 'wax',
      name: 'Waxing Packages',
      desc: 'Bundle multiple waxing areas for a better rate vs paying per service.',
      duration: 45,
      variant: 'packages',
      tiers: [
        { id: 'glow', label: 'Glow & Go · brows, lip, chin, sideburns', price: 60, ghlCalendarId: 'MT3XhZ26fFHiFweWqEW5' },
        { id: 'smooth', label: 'Smooth Essentials · underarms + bikini', price: 75, ghlCalendarId: 'vSDOlFMrhUE2g2U8IQIm' },
        { id: 'luxe', label: 'Total Body Luxe', price: 220, ghlCalendarId: 'jWLeNnwuT4Du0H2QNTGI' },
        { id: 'ultimate', label: 'Ultimate Silky · full body + Brazilian', price: 325, ghlCalendarId: 'Kv3pvk3FVzqf67Ai4hKx' },
      ],
    },
  ],
  body: [
    {
      id: 'rlt',
      name: 'Red Light Therapy (RF)',
      desc: 'Non-invasive low-wavelength red light that stimulates collagen, accelerates healing, and improves skin tone.',
      duration: 20,
      variant: 'sessions',
      tiers: [
        { id: 1, label: '1 session', price: 75, ghlCalendarId: 'Z7TkiyP9dCzIL6mFnyTN' },
        { id: 3, label: '3 sessions', price: 400, save: 25, ghlCalendarId: '5v11QC57qHobfvV3ePAz' },
        { id: 6, label: '6 sessions', price: 800, save: 50, ghlCalendarId: 'EYIyNWxKPQdTW7W1xpO8' },
        { id: 9, label: '9 sessions', price: 1215, save: 75, ghlCalendarId: 'W8MCqI4qVqpNoNH9Uvn5' },
      ],
    },
    {
      id: 'cav',
      name: 'Ultrasonic Cavitation',
      desc: 'Non-invasive ultrasound treatment that breaks down stubborn fat cells. Contour and slim without surgery or downtime.',
      duration: 60,
      variant: 'sessions',
      badge: 'Most booked',
      tiers: [
        { id: 1, label: '1 session', price: 145, ghlCalendarId: '58dipP1ajVmsPl08eM9e' },
        { id: 3, label: '3 sessions', price: 400, save: 35, ghlCalendarId: 'UyhxZTNbbCU1MVdPpjuO' },
        { id: 6, label: '6 sessions', price: 800, save: 70, ghlCalendarId: 'U9ggna6ARydFGjMpj0Ml' },
        { id: 9, label: '9 sessions', price: 1215, save: 90, ghlCalendarId: 'n5PojTS7g457au4iuW2e' },
      ],
    },
    {
      id: 'bbl',
      name: 'Vacuum Butt Lift Therapy',
      desc: 'Non-surgical vacuum therapy using suction and massage to enhance shape and fullness.',
      duration: 45,
      variant: 'sessions',
      tiers: [
        { id: 1, label: '1 session', price: 120, ghlCalendarId: '8JwVWV44deJ1HPm1OTu5' },
        { id: 3, label: '3 sessions', price: 300, save: 60, ghlCalendarId: '6jJIww2mjbkV4JrU6PDD' },
        { id: 6, label: '6 sessions', price: 650, save: 70, ghlCalendarId: 'ndWlFLAt54bgnaXwCjC3' },
        { id: 9, label: '9 sessions', price: 1000, save: 80, ghlCalendarId: 'DjCjYu0EakLIBS602vva' },
      ],
    },
    {
      id: 'wood',
      name: 'Wood Therapy Massage',
      desc: 'Holistic massage using wooden tools to reduce cellulite, sculpt the body, and stimulate lymphatic drainage.',
      duration: 60,
      variant: 'sessions',
      tiers: [
        { id: 1, label: '1 session', price: 120, ghlCalendarId: 'NRV8HoVwbKesvXOybXq1' },
        { id: 3, label: '3 sessions', price: 300, save: 60, ghlCalendarId: 'jAJjXHOuJSOo5IGRbaKW' },
        { id: 6, label: '6 sessions', price: 650, save: 70, ghlCalendarId: 'rhUXFr7PgkFy2LvoHP5X' },
        { id: 9, label: '9 sessions', price: 1000, save: 80, ghlCalendarId: 'BrsIPu3xleE97xbEfLid' },
      ],
    },
    {
      id: 'manual',
      name: 'Manual Body Contouring',
      desc: 'Achieve a slimmer, more defined physique with Body Contouring services. Great for post-surgery — reduces unwanted fat and improves body shape using advanced techniques tailored to your goals.',
      duration: 60,
      variant: 'sessions',
      tiers: [
        { id: 1, label: '1 session', price: 120, ghlCalendarId: 'iuFcEBPHJUwjpS12MTXs' },
        { id: 3, label: '3 sessions', price: 300, save: 60, ghlCalendarId: 'g3mt3ahzU48DhV987t7f' },
        { id: 6, label: '6 sessions', price: 650, save: 70, ghlCalendarId: 'hSEusoXVvIwS0zEYmS4I' },
        { id: 9, label: '9 sessions', price: 1000, save: 80, ghlCalendarId: 'HnjUSGuL8AA7Meu4OIVw' },
      ],
    },
    {
      id: 'lymph',
      name: 'Manual Lymphatic Drainage',
      desc: 'Promote overall wellness and reduce water retention. This gentle massage technique encourages lymphatic fluid flow, helping to detoxify the body and improve circulation. Always consult your doctor before beginning any post-surgery care treatment.',
      duration: 60,
      variant: 'sessions',
      tiers: [
        { id: 1, label: '1 session', price: 145, ghlCalendarId: '8jE6rvCmEWMmAD4ExZuR' },
        { id: 3, label: '3 sessions', price: 400, save: 35, ghlCalendarId: 'ZqvSQf9xtSJTVFNlmsIP' },
        { id: 6, label: '6 sessions', price: 800, save: 35, ghlCalendarId: 'mUO7xLLP1JjVhJgEjFSu' },
        { id: 9, label: '9 sessions', price: 1215, save: 25, ghlCalendarId: 'o4n32DMt4BFUPnMGO9ZV' },
      ],
    },
    LYMPHATIC_FACE_MASSAGE,
  ],
  weight: [
    {
      id: 'glp1-sema',
      name: 'Semaglutide GLP-1',
      desc: 'Medically supervised Ozempic®/Wegovy® program. Includes FREE required MD consultation, pharmacy dispensing, and discreet home delivery. No insurance required.',
      duration: 30,
      variant: 'subscription',
      tiers: [
        { id: 'mo',  label: 'Monthly',           price: 600,  unit: '/month', ghlCalendarId: 'ijXmxB5D9H629JX19Cg7' },
        { id: '3mo', label: '3-month program',   price: 1500, save: 300,      ghlCalendarId: 'ND2ZNaVJSYro4R4QFj1j' },
      ],
    },
    {
      id: 'glp1-tirz',
      name: 'Tirzepatide GLP-1',
      desc: 'Medically supervised Mounjaro®/Zepbound® program. Includes FREE required MD consultation, dispensing, and discreet home delivery.',
      duration: 30,
      variant: 'subscription',
      tiers: [
        { id: 'mo',  label: 'Monthly',           price: 750,  unit: '/month', ghlCalendarId: 'ndr2wihYORC7SOhYaJ9e' },
        { id: '3mo', label: '3-month program',   price: 2000, save: 250,      ghlCalendarId: '1vltihIeV6Jg6LS4XTxp' },
      ],
    },
  ],
  // Hidden — Be You Skin Club member-perk services. Free at point of
  // booking ($0 + depositType=NONE in WidgetServiceOverride triggers the
  // free-booking branch in cart.checkout). Only reachable via deep link.
  'skin-club': [
    {
      id: 'skin-club-micro',
      name: 'Microdermabrasion Facial',
      desc: 'Exfoliating treatment that removes the outer layer of skin, leaving skin smoother and refreshed. Included with your Skin Club membership.',
      duration: 30,
      variant: 'flat',
      price: 0,
      ghlCalendarId: '2MHnrTV8MD7IjqDSiREt',
    },
    {
      id: 'skin-club-rlt',
      name: 'Unlimited Red Light Therapy',
      desc: 'Low-level red light to promote skin rejuvenation, improve collagen, and reduce inflammation. Unlimited as part of your Skin Club membership.',
      duration: 15,
      variant: 'flat',
      price: 0,
      ghlCalendarId: '3uITlAebbkIeKQsCjdsg',
    },
    {
      id: 'skin-club-chair',
      name: 'Chair Massage',
      desc: 'A 15-minute relaxing chair massage included with your Skin Club membership.',
      duration: 15,
      variant: 'flat',
      price: 0,
      ghlCalendarId: 'zkqOO9xnqunpwsdpyI75',
    },
  ],
  // Hidden — Event Packages (deep-link-only). Full payment upfront to
  // lock the date; add-a-guest pricing handled off-widget by staff.
  events: [
    {
      id: 'events-party',
      name: 'Glow With the Flow Party Package',
      desc: '4-hour experience for 6 guests. Each receives: 1 Body Contouring Session · Be You Facial with Hydrating Collagen Mask · Hand & Foot Wellness · White Robe Experience · Artisan Pastry & Curated Sweet Bites · 3 Party-themed Games. Lounge. Sip. Be You.',
      duration: 240,
      variant: 'flat',
      price: 2700,
      ghlCalendarId: 'Wn3b7ietCgth4q92a3Rh',
    },
    {
      id: 'events-bridal',
      name: 'Something New, Something You · Bridal Party Package',
      desc: '4-hour bridal experience for 6 guests minimum. Each receives: Be You Facial with Hydrating Collagen Mask · Aromatherapy Touch Experience · 1 RED Light Therapy · White Robe Experience · Hand & Foot Wellness · Artisan Pastry & Curated Sweet Bites · 3 Party-themed Games. Lounge. Sip. Be You.',
      duration: 240,
      variant: 'flat',
      price: 2254,
      ghlCalendarId: 'GZYbiLahtMprLNYAJCkq',
    },
    {
      id: 'events-dual',
      name: 'Dual Glow-Up Experience',
      desc: '2-hour experience for 2 guests. Each receives: Microdermabrasion Glow Session · Body Contouring Session · RED Light Therapy · Hand & Foot Wellness · White Robe Experience. Lounge. Sip. Be You.',
      duration: 120,
      variant: 'flat',
      price: 815.99,
      ghlCalendarId: 'wYjUmCBHhRrL5mRgzU51',
    },
  ],
};

function fmtPrice(n: number | null | undefined): string {
  if (n == null) return '—';
  return n % 1 === 0 ? `$${n.toLocaleString()}` : `$${n.toFixed(2)}`;
}

function fmtDuration(min: number): string {
  return min < 60
    ? `${min}m`
    : `${Math.floor(min / 60)}h${min % 60 ? ` ${min % 60}m` : ''}`;
}

// ─────────────────────────────────────────────────────────────────────
// Category step — 2-column grid of category cards. Mirrors the design's
// BeYouCategoryStep verbatim.
// ─────────────────────────────────────────────────────────────────────

/**
 * Gather every service across all categories, tagged with its categoryId.
 * Used by tab filter + global search.
 */
const STATIC_CATALOG: Catalog = { categories: BEYOU_CATEGORIES, services: BEYOU_SERVICES };

function flattenAllServices(
  catalog: Catalog = STATIC_CATALOG,
): Array<BeYouService & { categoryId: string; categoryName: string }> {
  const out: Array<BeYouService & { categoryId: string; categoryName: string }> = [];
  // Dedupe by object identity, not id — a service listed in two categories
  // (Lymphatic Face Massage: face + body reference the same object) must
  // flatten once for the "All" tab / search / auto-expand, tagged with the
  // first category that carries it. Identity keeps distinct services that
  // happen to reuse an id string (tier ids like 'bbl') unaffected.
  const seen = new Set<BeYouService>();
  for (const c of catalog.categories) {
    // Skip soon-to-launch + hidden categories. Hidden categories are
    // reachable only by direct deep link, never by flattening (which
    // backs the "All" tab + search).
    if (c.soon || c.hidden) continue;
    const services = catalog.services[c.id] ?? [];
    for (const s of services) {
      if (seen.has(s)) continue;
      seen.add(s);
      out.push({ ...s, categoryId: c.id, categoryName: c.name });
    }
  }
  return out;
}

// Short tab labels — the full category names are too long for chips.
const TAB_LABELS: Record<string, string> = {
  face: 'Face & Skin',
  body: 'Body Contouring',
  weight: 'Weight Mgmt',
  rx: 'Prescriptions',
};

// ─────────────────────────────────────────────────────────────────────
// Service row — expandable card with variant pickers (tiers / areas /
// sessions / units / consult). Mirrors beyou-service-v2.jsx
// BeYouServiceRow exactly.
// ─────────────────────────────────────────────────────────────────────

interface ServiceRowProps {
  svc: BeYouService;
  selected: boolean;
  expanded: boolean;
  /**
   * Category label to surface in the meta line. Set when the row is
   * coming from cross-category search (so the user knows which category
   * the service belongs to). Null = render duration only.
   */
  categoryLabel?: string | null;
  /**
   * Called with the chosen tier's GHL calendar ID + price + deposit
   * + a display label (service + tier, e.g. "Botox · 2 areas · 40 units").
   * Null ghlCalendarId when the design tier has no live GHL match —
   * caller keeps Continue disabled. Price + deposit are in dollars.
   */
  onSelect: (
    ghlCalendarId: string | null,
    priceDollars: number,
    depositDollars: number,
    label: string,
    isConsult?: boolean,
    /** Phase 6 — set for a Special Service: bought, not booked. */
    productId?: string,
  ) => void;
  onToggle: () => void;
}

function BeYouServiceRow({ svc, selected, expanded, categoryLabel, onSelect, onToggle }: ServiceRowProps) {
  const initialTierId = svc.tiers ? svc.tiers[0]!.id : null;
  const initialAreaId = svc.areas ? svc.areas[0]!.id : null;
  const [tierId, setTierId] = useState<string | number | null>(initialTierId);
  const [areaId, setAreaId] = useState<string | null>(initialAreaId);
  const [units, setUnits] = useState(0);

  const area = svc.areas ? svc.areas.find((a) => a.id === areaId) ?? null : null;
  const tiers = area ? area.tiers : svc.tiers ?? null;
  const tier = tiers ? tiers.find((t) => t.id === tierId) ?? tiers[0] ?? null : null;

  // When the area changes, reset to its first tier if the current tier id
  // isn't in the new area's tier list. Runs as an effect (not at render
  // time) so we don't trigger a state mutation mid-render — keyed only on
  // areaId so the reset fires once per area swap.
  useEffect(() => {
    if (!area || !tier) return;
    if (!area.tiers.find((t) => t.id === tier.id)) {
      setTierId(area.tiers[0]!.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaId]);

  const computedPrice = (() => {
    if (svc.variant === 'flat') return svc.price ?? 0;
    if (tier) return tier.price + (svc.addon && units ? units * svc.addon.unit : 0);
    return 0;
  })();

  const fromPrice = (() => {
    if (svc.variant === 'flat') return svc.price ?? null;
    if (svc.variant === 'area_then_sessions') return svc.areas?.[0]?.tiers[0]?.price ?? null;
    if (svc.tiers) return svc.tiers[0]!.price;
    return null;
  })();

  return (
    <div class={'beyou-svc' + (selected ? ' is-selected' : '') + (expanded ? ' is-expanded' : '')}>
      <button type="button" class="beyou-svc-head" onClick={onToggle}>
        <div class="beyou-svc-head-l">
          <div class="beyou-svc-name">
            {svc.name}
            {svc.badge && (
              <span class="beyou-svc-badge-inline">
                <StarIcon size={9} /> {svc.badge}
              </span>
            )}
          </div>
          <div class="beyou-svc-meta">
            {categoryLabel ? `${categoryLabel} · ` : ''}
            {svc.metaLabel ?? fmtDuration(svc.duration)}
          </div>
        </div>
        <div class="beyou-svc-head-r">
          {svc.variant === 'consult' ? (
            <div class="beyou-svc-price beyou-svc-price--consult">Consult</div>
          ) : (
            <div class="beyou-svc-price">
              {svc.variant !== 'flat' && <span class="beyou-svc-price-pre">from</span>}
              {fmtPrice(fromPrice)}
            </div>
          )}
          <span class="beyou-svc-chev">{expanded ? '−' : '+'}</span>
        </div>
      </button>

      {expanded && (
        <div class="beyou-svc-body">
          <p class="beyou-svc-desc">{svc.desc}</p>

          {svc.variant === 'consult' ? (
            <button
              type="button"
              class="beyou-svc-consult-cta"
              onClick={() =>
                // Consult: no price yet, no deposit charged (it's a free
                // consultation flow). Pass 0/0 — running bar will show
                // "Continue" without a price ribbon. Label is just the
                // service name (no tier suffix on consults).
                onSelect(svc.ghlCalendarId ?? null, 0, 0, `${svc.name} · Consultation`, true)
              }
            >
              {selected ? 'Selected ✓' : 'Book a consultation →'}
            </button>
          ) : (
            <>
              {svc.variant === 'area_then_sessions' && svc.areas && (
                <div class="beyou-variant-block">
                  <div class="beyou-variant-label">Body area</div>
                  <div class="beyou-variant-list">
                    {svc.areas.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        class={'beyou-variant' + (a.id === areaId ? ' is-on' : '')}
                        onClick={() => setAreaId(a.id)}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {tiers && (
                <div class="beyou-variant-block">
                  <div class="beyou-variant-list beyou-variant-list--tiers">
                    {tiers.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        class={'beyou-tier' + (t.id === tierId ? ' is-on' : '')}
                        onClick={() => {
                          // Tap-to-select: the tier choice IS the
                          // commitment. Wire the tier's GHL calendar id
                          // up through to the booking flow immediately
                          // along with the price + computed deposit and
                          // a display label combining service name +
                          // (optional area) + tier label for the runbar.
                          setTierId(t.id);
                          if (t.productId) {
                            // A purchase: the full price, no deposit, no calendar.
                            onSelect(null, t.price, t.price, `${svc.name} · ${t.label}`, false, t.productId);
                          } else if (t.ghlCalendarId) {
                            const deposit = computeBeYouDepositDollars(svc.variant, t, t.price);
                            const labelParts = [svc.name];
                            if (area) labelParts.push(area.label);
                            labelParts.push(t.label);
                            onSelect(t.ghlCalendarId, t.price, deposit, labelParts.join(' · '));
                          }
                        }}
                        disabled={!t.ghlCalendarId && !t.productId}
                      >
                        <span class="beyou-tier-label">{t.label}</span>
                        {t.save && <span class="beyou-tier-save">Save ${t.save}</span>}
                        <span class="beyou-tier-price">
                          {fmtPrice(t.price)}
                          {t.unit && <span class="beyou-tier-unit">{t.unit}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {svc.addon && svc.variant === 'areas_count' && (
                <div class="beyou-variant-block">
                  <div class="beyou-variant-label">
                    {svc.addon.label} · ${svc.addon.unit}/unit
                  </div>
                  <div class="beyou-stepper">
                    <button type="button" onClick={() => setUnits(Math.max(0, units - 5))}>−</button>
                    <span class="beyou-stepper-v">{units}</span>
                    <button type="button" onClick={() => setUnits(units + 5)}>+</button>
                    <span class="beyou-stepper-l">+${units * svc.addon.unit}</span>
                  </div>
                </div>
              )}

              {svc.addon && svc.variant === 'flat' && (
                <div class="beyou-variant-block">
                  <div class="beyou-variant-label">
                    {svc.addon.label} · ${svc.addon.unit} each
                  </div>
                  <div class="beyou-stepper">
                    <button type="button" onClick={() => setUnits(Math.max(0, units - 1))}>−</button>
                    <span class="beyou-stepper-v">{units}</span>
                    <button type="button" onClick={() => setUnits(units + 1)}>+</button>
                    <span class="beyou-stepper-l">+${units * svc.addon.unit}</span>
                  </div>
                </div>
              )}

              {/* For variants with a tier picker, tapping the tier is
                  the commit gesture — no separate Select button. Flat
                  services (single fixed price, no tiers) keep the
                  bottom CTA as the only commit affordance. */}
              {svc.variant === 'flat' && (
                <button
                  type="button"
                  class="beyou-svc-select"
                  onClick={() => {
                    const flatPrice =
                      (svc.price ?? 0) + (svc.addon ? units * svc.addon.unit : 0);
                    const deposit = computeBeYouDepositDollars(svc.variant, null, flatPrice);
                    // Label = service name + the unit label if present
                    // (e.g. "Kybella Chin Treatment · per vial").
                    const label = svc.unitLabel
                      ? `${svc.name} · ${svc.unitLabel}`
                      : svc.name;
                    if (svc.productId) {
                      onSelect(null, flatPrice, flatPrice, svc.name, false, svc.productId);
                    } else {
                      onSelect(svc.ghlCalendarId ?? null, flatPrice, deposit, label);
                    }
                  }}
                  disabled={!svc.ghlCalendarId && !svc.productId}
                >
                  <span>{selected ? 'Selected ✓' : 'Select'}</span>
                  <span class="beyou-svc-select-p">
                    {fmtPrice(
                      computedPrice +
                        (svc.addon ? units * svc.addon.unit : 0),
                    )}
                  </span>
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Service step — list of services within the picked category, with the
// expandable cards above. Mirrors BeYouServiceStep.
// ─────────────────────────────────────────────────────────────────────

interface ServiceProps {
  /** 'all' or a categoryId — filters the flat service list. */
  activeTab: string;
  onChangeTab: (tab: string) => void;
  /** Design-side service id (e.g. 'micro', 'botox') currently selected. */
  selectedId: string | null;
  expandedId: string | null;
  /**
   * Called with (designServiceId, ghlCalendarId, priceCents, depositCents,
   * serviceLabel) when the user picks a tier or flat service. The widget
   * passes ghlCalendarId to the rest of the booking flow (slot search,
   * checkout); price + deposit drive the running-bar display; the
   * serviceLabel is the human-readable name + tier (e.g.
   * "Microdermabrasion Facial · 3 sessions") for the running bar.
   *
   * Deposit rule per the Be You product spec:
   *   - subscription / packages variants → full payment
   *   - multi-session tiers (save flag set OR tier id > 1) → full payment
   *   - single-session / flat services > $50 → $50 flat deposit
   *   - everything else → full payment (i.e. price ≤ $50)
   */
  onSelect: (
    designServiceId: string,
    ghlCalendarId: string | null,
    priceCents: number,
    depositCents: number,
    serviceLabel: string,
    isConsult?: boolean,
    productId?: string,
  ) => void;
  onExpand: (id: string | null) => void;
  /** Phase 6 — the salon's Special Services, from bootstrap. */
  shop?: ShopProduct[];
  /**
   * Optional initial search-bar value. Drives the `?q=<text>` deep-link
   * shape — CMS pages link to a specific service by name (e.g.
   * `?q=chemical+peel`) and the widget lands with the search filtered.
   * When the initial query produces exactly one design-service match,
   * that service row is auto-expanded so the tier picker is visible.
   */
  initialQuery?: string | null;
}

/**
 * Be You deposit policy. Returns the deposit amount in dollars.
 * Mirrors the rule above — single sessions of any service over $50 get
 * a $50 flat deposit; everything else is full payment.
 */
function computeBeYouDepositDollars(
  variant: BeYouService['variant'],
  tier: BeYouTier | null,
  priceDollars: number,
): number {
  // Subscription + bundled-packages variants always require full payment.
  if (variant === 'subscription') return priceDollars;
  if (variant === 'packages') return priceDollars;
  // Tiers with savings = explicit multi-session packages → full payment.
  if (tier?.save != null) return priceDollars;
  // Multi-session tiers (id > 1) on session-based variants → packages →
  // full payment. NOTE: gate to session-based variants only — `areas_count`
  // (Botox 2/3 areas) numeric tier ids are area COUNT, not session count;
  // those are single appointments and stay on the $50 deposit rule.
  if (
    tier &&
    typeof tier.id === 'number' &&
    tier.id > 1 &&
    (variant === 'sessions' || variant === 'area_then_sessions')
  ) {
    return priceDollars;
  }
  // Singles / flats: $50 deposit when over $50, else the full price.
  return priceDollars > 50 ? 50 : priceDollars;
}

export function BeYouServiceView({
  activeTab,
  onChangeTab,
  selectedId,
  expandedId,
  onSelect,
  onExpand,
  initialQuery,
  shop,
}: ServiceProps) {
  const catalog = useMemo(() => buildCatalog(STATIC_CATALOG, shop), [shop]);
  // Seed search-input from the `?q=<text>` deep link on mount.
  const [query, setQuery] = useState(initialQuery ?? '');
  const q = query.trim().toLowerCase();
  const all = flattenAllServices(catalog);
  const activeCategory = catalog.categories.find((c) => c.id === activeTab);
  const onHiddenCategory = activeCategory?.hidden === true;

  // Auto-expand on first render when the seeded query matches exactly
  // one design service — surfaces the tier picker so the customer can
  // pick + book without an extra tap. Only fires once (the autoExpanded
  // ref guards against re-fires when the user types further).
  const autoExpandedRef = useRef(false);
  useEffect(() => {
    if (autoExpandedRef.current) return;
    if (!initialQuery) return;
    const lower = initialQuery.trim().toLowerCase();
    if (!lower) return;
    const matches = all.filter(
      (s) =>
        s.name.toLowerCase().includes(lower) ||
        s.desc.toLowerCase().includes(lower),
    );
    if (matches.length === 1 && matches[0]) {
      autoExpandedRef.current = true;
      onExpand(matches[0].id);
    }
  // Run once on mount only — onExpand is stable from App.tsx's setState.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search overrides tab filter — typing always searches every category.
  // Empty query → filter by the active tab ('all' = no filter). Hidden
  // categories aren't in `all` (filtered out at flatten); when activeTab
  // is a hidden category we render its services directly from
  // BEYOU_SERVICES so the deep link still works.
  let visibleServices: ReturnType<typeof flattenAllServices>;
  if (q) {
    visibleServices = all.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.desc.toLowerCase().includes(q),
    );
  } else if (activeCategory) {
    // Source category tabs straight from the catalog, NOT the flatten —
    // the flatten dedupes services shared across categories (Lymphatic
    // Face Massage), which must still appear in EACH of their tabs. This
    // branch also covers hidden categories, which the flatten drops
    // entirely.
    const services = catalog.services[activeCategory.id] ?? [];
    visibleServices = services.map((s) => ({
      ...s,
      categoryId: activeCategory.id,
      categoryName: activeCategory.name,
    }));
  } else {
    // 'all' — the deduped flatten.
    visibleServices = all;
  }

  // Show the category badge in the row meta line whenever we're rendering
  // services from more than one category — i.e. during search OR on "All".
  const showCategoryLabel = !!q || activeTab === 'all';

  // Hidden categories don't render in the tab bar — they're deep-link-
  // only. The active hidden tab still appears as a header above the
  // service list so the user knows which collection they're viewing.
  const tabs: Array<{ id: string; label: string }> = [
    { id: 'all', label: 'All' },
    ...catalog.categories.filter((c) => !c.soon && !c.hidden).map((c) => ({
      id: c.id,
      label: TAB_LABELS[c.id] ?? c.name,
    })),
  ];

  return (
    <div class="sbw-scroll" style={{ paddingBottom: 120 }}>
      <h2 class="sbw-step-title">
        {onHiddenCategory && activeCategory ? activeCategory.name : 'Choose a treatment'}
      </h2>
      {onHiddenCategory && activeCategory?.sub && (
        <p class="sbw-step-sub" style={{ marginTop: -8, marginBottom: 16 }}>
          {activeCategory.sub}
        </p>
      )}

      {!onHiddenCategory && (
        <div class="sbw-cat-rail" role="tablist" aria-label="Service categories">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={t.id === activeTab}
              class={'sbw-cat' + (t.id === activeTab ? ' is-active' : '')}
              onClick={() => onChangeTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <div class="beyou-search beyou-search--svc">
        <span class="beyou-search-icon"><SearchIcon /></span>
        <input
          placeholder="Search — botox, cavitation, microdermabrasion…"
          value={query}
          onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
        />
      </div>

      <div class="beyou-svc-list">
        {visibleServices.length === 0 && (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: '#6e6660', fontFamily: 'Montserrat, sans-serif', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
            {q ? `No matches for "${query}"` : 'No services in this category'}
          </div>
        )}
        {visibleServices.map((s) => (
          <BeYouServiceRow
            key={`${s.categoryId}::${s.id}`}
            svc={s}
            selected={selectedId === s.id}
            expanded={expandedId === s.id}
            categoryLabel={showCategoryLabel ? s.categoryName : null}
            onSelect={(ghlId, priceDollars, depositDollars, label, isConsult, productId) =>
              onSelect(
                s.id,
                ghlId,
                Math.round(priceDollars * 100),
                Math.round(depositDollars * 100),
                label,
                isConsult,
                productId,
              )
            }
            onToggle={() => onExpand(expandedId === s.id ? null : s.id)}
          />
        ))}
      </div>
    </div>
  );
}
